import { FC, ReactNode, useState, useEffect, useRef } from 'react';

interface CubeRotatorProps {
  faces: ReactNode[];
  interval?: number;
  height: number;
  paused?: boolean;
}

const CubeRotator: FC<CubeRotatorProps> = ({ faces, interval = 8000, height, paused = false }) => {
  const [currentFace, setCurrentFace] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const faceCount = faces.length;

  useEffect(() => {
    if (paused || isHovered || faceCount < 2) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentFace((prev) => (prev + 1) % faceCount);
    }, interval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused, isHovered, faceCount, interval]);

  const tz = height / 2;

  return (
    <div
      className="w-full overflow-visible"
      style={{ perspective: '900px', height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="cube-rotator"
    >
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `rotateX(${currentFace * -90}deg)`,
        }}
      >
        {faces.map((face, i) => (
          <div
            key={i}
            className="absolute inset-0 w-full h-full"
            style={{
              backfaceVisibility: 'hidden',
              transform: `rotateX(${i * 90}deg) translateZ(${tz}px)`,
            }}
          >
            {face}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CubeRotator;
